import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { NAV, AUTH } from "@/constants/testIds";
import { LayoutDashboard, FilePlus, FileText, Inbox, Send as SendIcon, CheckCircle2, Library, ShieldAlert, LogOut, KeyRound, Search, Eye, EyeOff, FileText as FileIcon, File as DocIcon, X, Menu } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail, docPreviewUrl } from "@/lib/api";
import { toast } from "sonner";
import { InstallPrompt } from "@/components/eoffice/InstallPrompt";

const NavLink = ({ to, icon: Icon, children, testid, badge }) => {
  const { pathname, search } = useLocation();
  const isCreate = to.includes("?new=");
  // "Create File" nav highlights only when the query flag is present; it's a
  // trigger, not a page. All other nav items use path-based matching.
  const active = isCreate
    ? new URLSearchParams(search).get("new") === "1"
    : (pathname === to || (to !== "/" && pathname.startsWith(to.split("?")[0])));
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 px-4 py-2.5 border-l-2 text-[14px] font-medium transition-colors duration-200 ${
        active ? "border-l-[#1D3557] bg-[#EFEEE5] text-[#0A0A0A]" : "border-l-transparent text-[#5C5C54] hover:bg-[#F0EFE7] hover:text-[#0A0A0A]"
      }`}
    >
      <Icon size={16} strokeWidth={1.75} />
      <span className="flex-1">{children}</span>
      {badge != null && badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#E63946] text-white text-[10px] font-mono-eo font-bold flex items-center justify-center leading-none"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
};

function PasswordInput({ value, onChange, testId, toggleTestId, autoFocus = false }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testId}
        type={show ? "text" : "password"}
        required
        minLength={6}
        value={value}
        onChange={onChange}
        className="eo-input pr-10"
        autoFocus={autoFocus}
      />
      <button
        type="button"
        data-testid={toggleTestId}
        onClick={() => setShow((v) => !v)}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-[#5C5C54] hover:text-[#0A0A0A]"
        tabIndex={-1}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== confirm) { toast.error("Passwords do not match"); return; }
    if (nw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw(""); setConfirm("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[#DCD9D0] rounded-none">
        <DialogHeader><DialogTitle className="font-heading text-[22px]">Change password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div>
            <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Current password</label>
            <PasswordInput testId="chpw-current" toggleTestId="chpw-current-toggle" value={cur} onChange={(e) => setCur(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">New password</label>
            <PasswordInput testId="chpw-new" toggleTestId="chpw-new-toggle" value={nw} onChange={(e) => setNw(e.target.value)} />
          </div>
          <div>
            <label className="block text-[12px] font-mono-eo uppercase tracking-widest text-[#5C5C54] mb-1.5">Confirm new password</label>
            <PasswordInput testId="chpw-confirm" toggleTestId="chpw-confirm-toggle" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="eo-btn-ghost">Cancel</button>
            <button data-testid="chpw-submit" type="submit" disabled={busy} className="eo-btn-primary">{busy ? "Saving..." : "Update password"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function fmt(dt) {
  try {
    const d = new Date(dt);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
  } catch { return dt; }
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ files: [], documents: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults({ files: [], documents: [] }); return; }
    const ctl = new AbortController();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`, { signal: ctl.signal });
        setResults(data);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") { /* ignore */ }
      } finally { setLoading(false); }
    }, 250);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [q]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const goFile = (id) => {
    setOpen(false); setQ("");
    nav(`/files/${id}`);
  };
  const openDoc = (id) => {
    setOpen(false);
    window.open(docPreviewUrl(id, { inline: true }), "_blank", "noopener,noreferrer");
  };

  const hasResults = results.files.length > 0 || results.documents.length > 0;
  const showDropdown = open && q.trim().length >= 2;

  return (
    <div ref={boxRef} className="relative w-full max-w-[520px]">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A80]" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search files, notes, or depository documents..."
          className="w-full h-9 pl-9 pr-8 text-[13px] border border-[#DCD9D0] bg-white rounded-sm focus:outline-none focus:border-[#1D3557]"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A8A80] hover:text-[#0A0A0A]"
            aria-label="Clear search"
            data-testid="global-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {showDropdown && (
        <div
          data-testid="global-search-dropdown"
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#DCD9D0] shadow-lg z-40 max-h-[420px] overflow-auto eo-scroll"
        >
          {loading && (
            <div className="px-4 py-3 text-[12px] text-[#8A8A80] font-mono-eo">// Searching...</div>
          )}
          {!loading && !hasResults && (
            <div className="px-4 py-4 text-[13px] text-[#8A8A80]">No results for &quot;{q}&quot;.</div>
          )}
          {!loading && results.files.length > 0 && (
            <div>
              <div className="px-4 py-2 border-b border-[#DCD9D0] bg-[#FBFAF3] text-[10px] font-mono-eo uppercase tracking-widest text-[#8A8A80]">// Files ({results.files.length})</div>
              {results.files.map((f) => (
                <button
                  key={f.id}
                  data-testid={`search-file-${f.id}`}
                  onClick={() => goFile(f.id)}
                  className="w-full text-left flex items-start gap-3 px-4 py-2.5 border-b border-[#DCD9D0] hover:bg-[#FBFAF3]"
                >
                  <FileIcon size={15} className="text-[#1D3557] mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold truncate">{f.name}</div>
                    <div className="text-[11px] text-[#8A8A80] font-mono-eo truncate">
                      {f.approved ? "Closed" : f.status} · Held by {f.current_holder_name} · Updated {fmt(f.updated_at)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {!loading && results.documents.length > 0 && (
            <div>
              <div className="px-4 py-2 border-b border-[#DCD9D0] bg-[#FBFAF3] text-[10px] font-mono-eo uppercase tracking-widest text-[#8A8A80]">// Depository ({results.documents.length})</div>
              {results.documents.map((d) => (
                <button
                  key={d.id}
                  data-testid={`search-doc-${d.id}`}
                  onClick={() => openDoc(d.id)}
                  className="w-full text-left flex items-start gap-3 px-4 py-2.5 border-b border-[#DCD9D0] hover:bg-[#FBFAF3]"
                >
                  <DocIcon size={15} className="text-[#2A9D8F] mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold truncate">{d.title}</div>
                    <div className="text-[11px] text-[#8A8A80] font-mono-eo truncate">
                      {(d.extension || "").toUpperCase()} · {d.original_filename} · by {d.uploader_name}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [unopenedCount, setUnopenedCount] = useState(0);
  const { pathname, search } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [pathname, search]);

  // Poll the stats endpoint on route changes so the badge stays fresh when the
  // user opens a file (and its unopened count decrements).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/stats");
        if (alive) setUnopenedCount(data.shared_unopened || 0);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [pathname]);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[240px_1fr]">
      {menuOpen && <div data-testid="mobile-menu-overlay" onClick={() => setMenuOpen(false)} className="lg:hidden fixed inset-0 z-40 bg-black/30" />}
      <aside data-testid="app-sidebar" className={`fixed lg:sticky top-0 left-0 z-50 w-[260px] lg:w-auto h-screen border-r border-[#DCD9D0] bg-[#FBFAF3] flex flex-col transition-transform duration-200 lg:translate-x-0 ${menuOpen ? "translate-x-0 shadow-xl" : "-translate-x-full"}`}>
        <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="lg:hidden absolute top-3 right-3 p-1.5 text-[#5C5C54]"><X size={18} /></button>
        <div className="px-5 py-6 border-b border-[#DCD9D0]" data-testid={NAV.brand}>
          <img src="/dvc-cil-logo.jpg" alt="DVC-CIL Power Private Limited" className="h-10 w-10 object-contain shrink-0" />
          <div>
            <div className="font-heading text-[16px] font-bold tracking-tight text-[#1D3557] leading-tight">DVC-CIL Power</div>
            <div className="font-mono-eo text-[9px] uppercase text-[#8A8A80] tracking-widest mt-0.5">Private Limited · e-Office</div>
          </div>
        </div>
        <nav className="py-4 flex-1 overflow-y-auto">
          <NavLink to="/" icon={LayoutDashboard} testid={NAV.dashboard}>Dashboard</NavLink>
          <NavLink to="/files/my?new=1" icon={FilePlus} testid={NAV.createFile}>Create File</NavLink>
          <NavLink to="/files/my" icon={FileText} testid={NAV.myFiles}>My Files</NavLink>
          <NavLink to="/files/shared" icon={Inbox} testid={NAV.shared} badge={unopenedCount}>Inbox</NavLink>
          <NavLink to="/files/sent" icon={SendIcon} testid={NAV.sent}>Sent</NavLink>
          <NavLink to="/files/closed" icon={CheckCircle2} testid={NAV.approved}>Closed</NavLink>
          <NavLink to="/depository" icon={Library} testid={NAV.depository}>Depository</NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" icon={ShieldAlert} testid={NAV.admin}>Admin</NavLink>
          )}
        </nav>
        <div className="border-t border-[#DCD9D0] p-4">
          <div className="flex items-center gap-3 mb-3" data-testid={NAV.profileMenu}>
            <div className="w-9 h-9 bg-[#1D3557] text-white flex items-center justify-center font-heading font-bold text-[14px] rounded-sm">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-[#8A8A80] font-mono-eo truncate">{user?.designation}</div>
            </div>
          </div>
          <button
            data-testid="change-password-btn"
            onClick={() => setPwOpen(true)}
            className="eo-btn-ghost w-full flex items-center justify-center gap-2 text-[13px] mb-2"
          >
            <KeyRound size={14} /> Change password
          </button>
          <button
            data-testid={AUTH.logoutBtn}
            onClick={async () => { await logout(); nav("/login"); }}
            className="eo-btn-ghost w-full flex items-center justify-center gap-2 text-[13px]"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>
      <main className="min-h-screen bg-[#F5F5F0] min-w-0">
        <div className="sticky top-0 z-30 bg-[#FBFAF3]/95 backdrop-blur border-b border-[#DCD9D0] px-3 lg:px-6 py-2.5 flex items-center gap-2 h-[57px]">
          <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)} className="lg:hidden h-9 w-9 shrink-0 flex items-center justify-center border border-[#DCD9D0] bg-white" aria-label="Open menu">
            <Menu size={18} />
            {unopenedCount > 0 && <span className="absolute ml-6 -mt-6 w-2.5 h-2.5 rounded-full bg-[#E63946]" />}
          </button>
          <GlobalSearch />
        </div>
        {children}
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <InstallPrompt />
    </div>
  );
}
